/**
 * Wave 15 — Cap campaign SoR desk (OTA Rail A Director).
 * Soft≠Live: only paints Live /ota/cap-campaigns*; no Soft invent rows.
 * execute_global / campaign_sor from backend verb matrix only.
 */
import { useEffect, useMemo, useState } from "react";
import { html, Alert, Field, Input, KV } from "./ui.js";
import { useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import { actionReceiptOf, isWriteDeniedStatus } from "./cap-complete.js";

function softCapErr(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That Cap campaign action could not be completed.";
  if (/403|forbidden|sa_ota|sor_disabled/.test(low)) {
    return "Your seat cannot run fleet Cap campaigns. Ask an OTA admin.";
  }
  if (/explicit_scope|no_targets|claimed_required/.test(low)) {
    return "Pick explicit devices or a group, and at least one capability slug.";
  }
  if (/invalid_transition|execute_forbidden/.test(low)) {
    return "That state change is not allowed yet.";
  }
  return n.length > 160 ? n.slice(0, 157) + "…" : n;
}

export function CapCampaignDesk({ toast, seat, initialDeviceIds, initialGroupKey }) {
  const st = useApi("/ota/cap-campaigns/status");
  const list = useApi(seat && seat.executeGlobal ? "/ota/cap-campaigns?limit=40" : "");
  const act = useAction();
  const envelope = act.envelope;
  const [deviceIds, setDeviceIds] = useState("");
  const [groupKey, setGroupKey] = useState("");
  const [tenantId, setTenantId] = useState("croc-qa-admin");
  const [claimed, setClaimed] = useState("snapshot");
  const [canaryN, setCanaryN] = useState("1");
  const [waveSize, setWaveSize] = useState("5");

  useEffect(() => {
    if (Array.isArray(initialDeviceIds)) setDeviceIds(initialDeviceIds.join(", "));
    else if (initialDeviceIds != null) setDeviceIds(String(initialDeviceIds));
  }, [initialDeviceIds]);

  useEffect(() => {
    if (initialGroupKey != null) setGroupKey(String(initialGroupKey));
  }, [initialGroupKey]);

  const items = useMemo(() => {
    const raw = list.data && list.data.items;
    return Array.isArray(raw) ? raw : [];
  }, [list.data]);

  const armed = !!(st.data && st.data.campaign_sor && st.data.execute_global);
  if (!seat || !seat.executeGlobal) {
    return html`<${Alert} tone="warn" title="Cap campaigns sealed"
      say="Fleet Cap execute is off for this seat. Scoped Cap stays on Skills warehouse / Machine."/>`;
  }
  if (st.loading && !st.data) {
    return html`<p className="note">Reading Cap campaigns…</p>`;
  }
  if (!armed) {
    return html`<${Alert} tone="warn" title="Cap campaigns sealed"
      say="Server reports Cap campaign execute is off. Use scoped Cap on Machine or Skills warehouse."/>`;
  }

  const refresh = () => {
    st.reload && st.reload();
    list.reload && list.reload();
  };

  const create = async () => {
    const ids = String(deviceIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const body = {
      claimed: String(claimed || "")
        .split(/[\s,]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
      device_ids: ids,
      group_key: String(groupKey || "").trim(),
      tenant_id: String(tenantId || "").trim(),
      canary_n: Number(canaryN) || 1,
      wave_size: Number(waveSize) || 5,
      note: "cmc-rail-a-cap-campaign",
    };
    const out = await act.run("/ota/cap-campaigns", { method: "POST", body });
    if (!out) {
      toast && toast(softCapErr(act.error), isWriteDeniedStatus(act.error) ? "warn" : "bad");
      return;
    }
    toast && toast(actionReceiptOf(out, "Cap campaign draft"), "go");
    setDeviceIds("");
    refresh();
  };

  const move = async (id, state) => {
    const out = await act.run("/ota/cap-campaigns/" + encodeURIComponent(id) + "/transition", {
      method: "POST",
      body: { state },
    });
    if (!out) {
      toast && toast(softCapErr(act.error), isWriteDeniedStatus(act.error) ? "warn" : "bad");
      return;
    }
    toast && toast(actionReceiptOf(out, "Cap campaign → " + state), "go");
    refresh();
  };

  const execWave = async (id) => {
    const ok = await confirmDeviceCommand({
      kind: "cap_execute_wave",
      targets: [{ id: String(id), label: "Cap campaign " + String(id) }],
      detail: "Sends the next capability wave to the selected devices. The server re-checks targets before dispatch.",
      confirmLabel: "Execute wave",
    });
    if (!ok) return;
    const out = await act.run("/ota/cap-campaigns/" + encodeURIComponent(id) + "/execute-wave", {
      method: "POST",
      body: {},
    });
    if (!out) {
      toast && toast(softCapErr(act.error), isWriteDeniedStatus(act.error) ? "warn" : "bad");
      return;
    }
    const green = out.physical_observed === true || out.device_ack_observed === true;
    toast && toast(
      actionReceiptOf(
        out,
        green
          ? ("Wave observed · dispatched " + (out.dispatched || 0))
          : ("Wave commands sent · awaiting ACK · dispatched " + (out.dispatched || 0)),
      ),
      green ? "go" : "warn",
    );
    refresh();
  };

  return html`<div className="cap-campaign-desk desk-craft" data-cmc-cap-sor="v589">
    <${Alert} tone="info" title="Capability campaigns"
      say="Feature packs for devices — separate from board image updates. Pick explicit devices or a group. Execute needs an OTA admin seat."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}
    <div className="grid2" style=${{ gap: 12, marginTop: 12 }}>
      <${Field} label="Tenant owner">
        <${Input} value=${tenantId} onInput=${(e) => setTenantId(e.target.value)} placeholder="owner_admin"/>
      </${Field}>
      <${Field} label="Group key (optional)">
        <${Input} value=${groupKey} onInput=${(e) => setGroupKey(e.target.value)} placeholder="rf-dispatch-alpha"/>
      </${Field}>
      <${Field} label="Device ids (space/comma)">
        <${Input} value=${deviceIds} onInput=${(e) => setDeviceIds(e.target.value)} placeholder="SN-QA-…"/>
      </${Field}>
      <${Field} label="Claimed caps">
        <${Input} value=${claimed} onInput=${(e) => setClaimed(e.target.value)} placeholder="snapshot,video"/>
      </${Field}>
      <${Field} label="Canary N">
        <${Input} value=${canaryN} onInput=${(e) => setCanaryN(e.target.value)}/>
      </${Field}>
      <${Field} label="Wave size">
        <${Input} value=${waveSize} onInput=${(e) => setWaveSize(e.target.value)}/>
      </${Field}>
    </div>
    <div className="btn-row" style=${{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
      <button type="button" className="btn btn--go btn--sm" disabled=${act.busy} onClick=${create}>
        Create draft</button>
      <button type="button" className="btn btn--ghost btn--sm" onClick=${refresh}>Refresh</button>
    </div>
    <div style=${{ marginTop: 16 }}>
      ${!items.length
        ? html`<p className="note">No Cap campaigns yet.</p>`
        : items.map((c) => html`<div key=${c.campaign_id} className="card" style=${{ marginBottom: 10, padding: 12 }}>
            <div><strong>${c.campaign_id}</strong> · ${c.state} · tenant ${c.tenant_scope}</div>
            <div className="note">digest ${(c.digest || "").slice(0, 12)} · canary ${c.canary_n} · wave ${c.wave_size} · idx ${c.wave_index}</div>
            <div className="btn-row" style=${{ marginTop: 8, gap: 6, flexWrap: "wrap" }}>
              ${c.state === "draft" && html`<button type="button" className="btn btn--ghost btn--sm" onClick=${() => move(c.campaign_id, "validated")}>Validate</button>`}
              ${c.state === "validated" && html`<button type="button" className="btn btn--ghost btn--sm" onClick=${() => move(c.campaign_id, "staged")}>Stage</button>`}
              ${(c.state === "staged" || c.state === "observing" || c.state === "canary" || c.state === "wave_running") && html`<button type="button" className="btn btn--go btn--sm" onClick=${() => execWave(c.campaign_id)}>Execute wave</button>`}
              ${c.state !== "halted" && c.state !== "archived" && html`<button type="button" className="btn btn--ghost btn--sm" onClick=${() => move(c.campaign_id, "halted")}>Halt</button>`}
            </div>
          </div>`)}
    </div>
  </div>`;
}

export default CapCampaignDesk;
